import React, { useCallback, useState } from "react";
import {
  useGetInterestingWords,
  SnippetView,
  SnippetsForSourceView,
} from "../api-generated";
import CenteredLoading from "./CenteredLoading";
import ErrorLoading from "./ErrorLoading";
import { useCurrentDevice, useKeyPressEvent } from "../hooks";
import Header from "./Header";
import clsx from "clsx";
import SourceTypeIcon from "./SourceTypeIcon";
import { Link } from "react-router-dom";
import { getPlatzSourceLink } from "../utils/navigation";
import { getTokenString } from "../utils";
import { getUniqueLocationId } from "../fe_types";
import InterestingWordInContext from "./InterestingWordInContext";
import { INTERESTING_THINGS_QUERY_KEY } from "../domain_data";

const Wrapped: React.FC = () => {
  const { data, isLoading, isError, error } = useGetInterestingWords({
    query: { queryKey: INTERESTING_THINGS_QUERY_KEY },
  });

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (isError) {
    return <ErrorLoading error={error} />;
  }

  return <InterestingWords wordsBySource={data} />;
};

interface Props {
  wordsBySource: SnippetsForSourceView[];
}

const InterestingWords: React.FC<Props> = ({ wordsBySource }) => {
  const { isComp } = useCurrentDevice();
  const [focusedSourceIndex, setFocusedSourceIndex] = useState(isComp ? 0 : -1);
  const [focusedSnippetIndex, setFocusedSnippetIndex] = useState(
    isComp ? 0 : -1
  );

  const [selectedSnippet, setSelectedSnippet] = useState<
    SnippetView | undefined
  >(undefined);

  const toggleOpenWord = useCallback(
    (sourceIndex: number, wordIndex: number): void => {
      const newWord: SnippetView =
        wordsBySource[sourceIndex].snippets[wordIndex];
      setSelectedSnippet((prev) => {
        if (prev === undefined) {
          return newWord;
        }
        return getUniqueLocationId(prev.sourceLocation) ===
          getUniqueLocationId(newWord.sourceLocation)
          ? undefined
          : newWord;
      });
    },
    [wordsBySource]
  );

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (wordsBySource.length === 0) {
        return;
      }
      if (event.key === "j") {
        setFocusedSourceIndex((prevIndex) =>
          Math.min(wordsBySource.length - 1, prevIndex + 1)
        );
        setFocusedSnippetIndex(0);
      } else if (event.key === "k") {
        setFocusedSourceIndex((prevIndex) => Math.max(0, prevIndex - 1));
        setFocusedSnippetIndex(0);
      } else if (event.key === "w") {
        setFocusedSnippetIndex((prevIndex) => {
          return Math.min(
            wordsBySource[focusedSourceIndex].snippets.length - 1,
            prevIndex + 1
          );
        });
      } else if (event.key === "b") {
        setFocusedSnippetIndex((prevIndex) => Math.max(0, prevIndex - 1));
      } else if (event.key === "Enter") {
        toggleOpenWord(focusedSourceIndex, focusedSnippetIndex);
      } else if (event.key === "c" && event.ctrlKey) {
        setSelectedSnippet(undefined);
      }
    },
    [
      JSON.stringify(wordsBySource),
      focusedSourceIndex,
      focusedSnippetIndex,
      toggleOpenWord,
      setSelectedSnippet,
    ]
  );

  useKeyPressEvent(
    {
      id: "interesting-words",
      priority: 0,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    isComp
  );

  if (wordsBySource.length === 0) {
    return <div>No interesting words from last 12 hours - go consume more</div>;
  }

  return (
    <>
      <div className="py-4">
        <Header text="Interesting Words from Last 12 Hours" type="main" />
      </div>
      <div className="flex justify-between gap-2">
        <div className="flex flex-col divide-y divide-gray-200 w-1/2">
          {wordsBySource.map((wordsForSource, sourceIndex) => {
            return (
              <div className="py-4" key={wordsForSource.source.id}>
                <Link to={getPlatzSourceLink(wordsForSource.source)}>
                  <div className="flex gap-4 pb-2">
                    <SourceTypeIcon type={wordsForSource.source.type} />
                    <Header type="sub" text={wordsForSource.source.name} />
                  </div>
                </Link>
                <div className="flex flex-wrap">
                  {wordsForSource.snippets.map((snippet, snippetIndex) => {
                    if (snippet.wordAndItsIndex === undefined) {
                      throw new Error("this shouldn't happen - illegal state");
                    }
                    return (
                      <div key={snippetIndex}>
                        <button
                          className={clsx("hover:pointer", {
                            "bg-gray-200":
                              focusedSnippetIndex === snippetIndex &&
                              focusedSourceIndex === sourceIndex,
                          })}
                          onClick={() =>
                            toggleOpenWord(sourceIndex, snippetIndex)
                          }
                        >
                          <div
                            className={clsx("py-1 px-2 text-left", {
                              "text-purple-600":
                                focusedSnippetIndex === snippetIndex &&
                                focusedSourceIndex === sourceIndex,
                            })}
                          >
                            {getTokenString(
                              snippet.text.tokens[
                                snippet.wordAndItsIndex.second
                              ]
                            )}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="w-1/2">
          {selectedSnippet ? (
            <InterestingWordInContext snippet={selectedSnippet} />
          ) : (
            <div className="py-12 flex justify-center">Nothing shown</div>
          )}
        </div>
      </div>
    </>
  );
};

export default Wrapped;
